import { useEffect, useState } from "react";
import {
  ChevronDown,
  Upload,
  Users,
  Zap,
  Briefcase,
  FileText,
  User,
  Settings as SettingsIcon,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  UserCheck,
} from "lucide-react";
import { Listbox, Transition } from "@headlessui/react";
import AskGenziButton from "./AskGenziButton";

import api from "../../services/api";
import { toast } from "react-toastify";
import tokenManager from "../../utils/tokenManager";
import { useUser } from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";

const countries = [
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Japan",
  "China",
];

const Toggle = ({ enabled, onChange }) => {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-[#0C0E12] ${
        enabled ? "bg-purple-600" : "bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
};

const NotificationConfigCard = ({
  title,
  description,
  icon,
  notificationType,
  config,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [enabled, setEnabled] = useState(config?.enabled ?? true);
  const [recipients, setRecipients] = useState(config?.recipients || []);
  const [newRecipient, setNewRecipient] = useState("");

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setRecipients(config.recipients || []);
    }
  }, [config]);

  const handleToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    await onUpdate(notificationType, { enabled: newEnabled, recipients });
  };

  const handleAddRecipient = () => {
    if (newRecipient && !recipients.includes(newRecipient)) {
      const updatedRecipients = [...recipients, newRecipient];
      setRecipients(updatedRecipients);
      setNewRecipient("");
      onUpdate(notificationType, { enabled, recipients: updatedRecipients });
    }
  };

  const handleRemoveRecipient = (email) => {
    const updatedRecipients = recipients.filter((r) => r !== email);
    setRecipients(updatedRecipients);
    onUpdate(notificationType, { enabled, recipients: updatedRecipients });
  };

  return (
    <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className="text-3xl">{icon}</div>
          <div className="flex-1">
            <h3 className="text-white font-medium text-base mb-1">{title}</h3>
            <p className="text-gray-400 text-sm">{description}</p>

            {isExpanded && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    Email Recipients
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddRecipient()
                      }
                      placeholder="Enter email address"
                      className="flex-1 bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                    <button
                      onClick={handleAddRecipient}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                    >
                      Add
                    </button>
                  </div>

                  <div className="space-y-2">
                    {recipients.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">
                        No recipients configured
                      </p>
                    ) : (
                      recipients.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between bg-[#1a1d23] rounded-lg px-3 py-2"
                        >
                          <span className="text-white text-sm">{email}</span>
                          <button
                            onClick={() => handleRemoveRecipient(email)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <button
                  onClick={() => setIsExpanded(false)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Hide Details
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isExpanded && recipients.length > 0 && (
            <span className="text-gray-400 text-sm">
              {recipients.length} recipient{recipients.length !== 1 ? "s" : ""}
            </span>
          )}

          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-gray-400 hover:text-white p-2"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          )}

          <Toggle enabled={enabled} onChange={handleToggle} />
        </div>
      </div>
    </div>
  );
};
function SettingsPage() {
  const [activeTab, setActiveTab] = useState("my-details");
  const { currentUser, refreshUser } = useUser();
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const navigate = useNavigate();

  // Personal details form data
  const [personalDetails, setPersonalDetails] = useState({
    name: "",
    email: "",
    profileImageUrl: "",
  });

  // Workspace form data
  const [workspaceDetails, setWorkspaceDetails] = useState({
    companyName: "",
    workspaceUrl: "",
    logoUrl: "",
  });
  const [notificationConfigs, setNotificationConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  // Fetch configs when tab is active
  useEffect(() => {
    if (activeTab === "email-notifications") {
      fetchNotificationConfigs();
    }
  }, [activeTab]);

  const fetchNotificationConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const response = await api.getNotificationConfigs();
      setNotificationConfigs(response.configs);
    } catch (error) {
      console.error("Failed to fetch notification configs:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoadingConfigs(false);
    }
  };

  const handleUpdateNotificationConfig = async (notificationType, updates) => {
    try {
      await api.updateNotificationConfig(notificationType, updates);
      toast.success("Notification settings updated");
      fetchNotificationConfigs();
    } catch (error) {
      console.error("Failed to update notification config:", error);
      toast.error("Failed to update notification settings");
    }
  };

  // Fetch current user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoadingUser(true);
        // Get fresh user data from API
        const userData = await api.getMe();

        // Set personal details
        setPersonalDetails({
          name: userData.user.name || "",
          email: userData.user.email || "",
          profileImageUrl: userData.user.profileImageUrl || "",
        });

        // Set workspace details if user has company
        if (userData.user.company) {
          setWorkspaceDetails({
            companyName: userData.user.company.name || "",
            workspaceUrl: userData.user.company.workspaceUrl || "",
            logoUrl: userData.user.company.logoUrl || "",
          });
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        toast.error("Failed to load user data");
      } finally {
        setIsLoadingUser(false);
      }
    };

    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser?.id]); // Add currentUser as dependency

  // Handle personal details update
  const handleUpdatePersonalDetails = async () => {
    try {
      const response = await api.updatePersonalDetails({
        name: personalDetails.name,
        email: personalDetails.email,
      });

      // Update local storage and state
      tokenManager.setUser(response.user);
      refreshUser(); // Refresh user in context

      toast.success("Personal details updated successfully");
    } catch (error) {
      console.error("Failed to update personal details:", error);
      toast.error(error.response?.data?.message || "Failed to update details");
    }
  };

  // Handle workspace details update
  const handleUpdateWorkspaceDetails = async () => {
    try {
      const response = await api.updateCompanyDetails({
        name: workspaceDetails.companyName,
        workspaceUrl: workspaceDetails.workspaceUrl,
      });

      // Update current user's company info
      refreshUser(); // Refresh user in context

      toast.success("Workspace details updated successfully");
    } catch (error) {
      console.error("Failed to update workspace details:", error);
      toast.error(
        error.response?.data?.message || "Failed to update workspace",
      );
    }
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await api.uploadProfileImage(formData);

      setPersonalDetails((prev) => ({
        ...prev,
        profileImageUrl: response.profileImageUrl,
      }));

      // Update user in local storage and context
      const updatedUser = {
        ...currentUser,
        profileImageUrl: response.profileImageUrl,
      };
      tokenManager.setUser(updatedUser);
      refreshUser(); // Refresh user in context

      toast.success("Profile image uploaded successfully");
    } catch (error) {
      console.error("Failed to upload profile image:", error);
      toast.error("Failed to upload profile image");
    }
  };

  // Handle company logo upload
  const handleCompanyLogoUpload = async (file) => {
    try {
      const response = await api.uploadAsset(file);

      setWorkspaceDetails((prev) => ({
        ...prev,
        logoUrl: response.url,
      }));

      toast.success("Company logo uploaded successfully");
    } catch (error) {
      console.error("Failed to upload company logo:", error);
      toast.error("Failed to upload company logo");
    }
  };

  const [integrationStatuses, setIntegrationStatuses] = useState({
    "Google Meet": false,
    "Microsoft Teams": false,
    Zoom: false,
  });
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);

  const integrations = [
    { name: "Google Meet", id: "google-meet", route: "/google" },
    { name: "Microsoft Teams", id: "microsoft-teams", route: "/teams" },
    { name: "Zoom", id: "zoom", route: "/zoom" },
    { name: "Webex", id: "webex", route: "/webex" },
  ];

  // Fetch integration statuses
  useEffect(() => {
    const fetchIntegrationStatuses = async () => {
      if (activeTab !== "integrations") return;

      try {
        setIsLoadingIntegrations(true);

        const googleStatus = await api.getGoogleStatus();
        const teamsStatus = await api.getTeamsStatus();
        const webexStatus = await api.getWebexStatus(); // Add this

        setIntegrationStatuses({
          "Google Meet": googleStatus.connected || false,
          "Microsoft Teams": teamsStatus.connected || false,
          Zoom: false,
          Webex: webexStatus.connected || false, // Add this
        });
      } catch (error) {
        console.error("Failed to fetch integration statuses:", error);
        setIntegrationStatuses({
          "Google Meet": false,
          "Microsoft Teams": false,
          Zoom: false,
          Webex: false,
        });
      } finally {
        setIsLoadingIntegrations(false);
      }
    };

    fetchIntegrationStatuses();
  }, [activeTab]);

  // Logo mapping function for public assets
  const getLogoForIntegration = (id) => {
    const logoMap = {
      "google-meet": "/assets/meets.png",
      "microsoft-teams": "/assets/teams.png",
      zoom: "/assets/zoom.png",
      salesforce: "/assets/salesforce.png",
      hubspot: "/assets/hubspot.png",
      webex: "/assets/cisco.png",
    };
    return logoMap[id];
  };

  const handleIntegrationToggle = async (integration) => {
    if (integration.id === "webex") {
      try {
        if (integrationStatuses["Webex"]) {
          // Disconnect
          if (window.confirm("Are you sure you want to disconnect Webex?")) {
            await api.disconnectWebex();
            setIntegrationStatuses((prev) => ({
              ...prev,
              Webex: false,
            }));
            toast.success("Webex disconnected successfully");
          }
        } else {
          // Connect - get auth URL
          const response = await api.connectWebex();
          window.location.href = response.authUrl;
        }
      } catch (error) {
        console.error("Webex integration error:", error);
        toast.error("Failed to toggle Webex integration");
      }
    } else {
      navigate(integration.route);
    }
  };

  // Dynamic tabs based on user role
  const tabs = [
    { id: "my-details", label: "My details", icon: User },
    ...(currentUser?.role === "admin" || currentUser?.role === "super_admin"
      ? [
          { id: "workspace", label: "Workspace", icon: Briefcase },
          { id: "team", label: "Team", icon: Users },
          {
            id: "email-notifications",
            label: "Email Notifications",
            icon: Mail,
          },
        ]
      : []),
    { id: "integrations", label: "Integrations", icon: Zap },
    { id: "ai-preferences", label: "AI preferences", icon: SettingsIcon },
  ];

  // Rest of your state variables...
  const [enableSummarisation, setEnableSummarisation] = useState(true);
  const [enableActionItems, setEnableActionItems] = useState(true);
  const [enableSentimentDetection, setEnableSentimentDetection] =
    useState(true);
  const [enableAutoTagging, setEnableAutoTagging] = useState(true);
  const [selectedModel, setSelectedModel] = useState("GPT-4o");
  const [summaryTone, setSummaryTone] = useState("Professional");
  const [targetSpeaker, setTargetSpeaker] = useState("Both");
  const [selectedLabels, setSelectedLabels] = useState([
    "Billing",
    "Tech support",
    "Escalation",
    "Payment",
  ]);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isToneDropdownOpen, setIsToneDropdownOpen] = useState(false);
  const [isLabelsDropdownOpen, setIsLabelsDropdownOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    const fetchTeamData = async () => {
      // Only fetch team data if user is admin
      if (
        currentUser?.role === "admin" ||
        currentUser?.role === "super_admin"
      ) {
        try {
          setIsLoading(true);
          const response = await api.getTeamMembers();
          setTeamMembers(response.users);
          setPendingInvitations(response.pendingInvitations);
        } catch (error) {
          console.error("Failed to fetch team data:", error);
          toast.error("Failed to load team data");
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      fetchTeamData();
    }
  }, [currentUser]);

  // Your existing team management functions...
  const handleInviteTeamMember = async () => {
    try {
      await api.inviteTeamMember(inviteEmail, inviteRole);
      const response = await api.getTeamMembers();
      setTeamMembers(response.users);
      setPendingInvitations(response.pendingInvitations);
      setShowInviteModal(false);
      setInviteEmail("");
      toast.success("Invitation sent successfully");
    } catch (error) {
      console.error("Failed to send invitation:", error);
      toast.error(error.response?.data?.message || "Failed to send invitation");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      const response = await api.getTeamMembers();
      setTeamMembers(response.users);
      toast.success("Role updated successfully");
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error(error.response?.data?.message || "Failed to update role");
    }
  };

  const handleRemoveUser = async (userId) => {
    if (window.confirm("Are you sure you want to remove this user?")) {
      try {
        await api.removeUser(userId);
        const response = await api.getTeamMembers();
        setTeamMembers(response.users);
        toast.success("User removed successfully");
      } catch (error) {
        console.error("Failed to remove user:", error);
        toast.error(error.response?.data?.message || "Failed to remove user");
      }
    }
  };
  // Add this function near your other team management functions
  const handleUpdateExtension = async (userId, extensionNumber) => {
    try {
      await api.updateUserExtension(userId, extensionNumber);

      // Refresh the team data to show the updated extension
      const response = await api.getTeamMembers();
      setTeamMembers(response.users);

      toast.success("Extension updated successfully");
    } catch (error) {
      console.error("Failed to update extension:", error);
      toast.error(
        error.response?.data?.message || "Failed to update extension",
      );
      throw error; // Re-throw so the UserRow component can handle it
    }
  };
  const handleResendInvitation = async (invitationId) => {
    try {
      await api.resendInvitation(invitationId);
      toast.success("Invitation resent successfully");
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      toast.error(
        error.response?.data?.message || "Failed to resend invitation",
      );
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (window.confirm("Are you sure you want to cancel this invitation?")) {
      try {
        await api.cancelInvitation(invitationId);
        const response = await api.getTeamMembers();
        setPendingInvitations(response.pendingInvitations);
        toast.success("Invitation cancelled successfully");
      } catch (error) {
        console.error("Failed to cancel invitation:", error);
        toast.error(
          error.response?.data?.message || "Failed to cancel invitation",
        );
      }
    }
  };

  // Your existing component definitions (Toggle, Dropdown, etc.)...
  const models = ["GPT-4o", "GPT-3.5", "Claude-3", "PaLM-2"];
  const tones = ["Professional", "Casual", "Friendly", "Formal"];
  const availableLabels = [
    "Billing",
    "Tech support",
    "Escalation",
    "Payment",
    "Sales",
    "Complaint",
    "Feature Request",
  ];

  const Toggle = ({ enabled, onChange }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        enabled ? "bg-[#7F56D9]" : "bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  const Dropdown = ({
    isOpen,
    setIsOpen,
    value,
    options,
    onChange,
    placeholder,
  }) => (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-left text-white focus:outline-none focus:border-[#7F56D9] flex items-center justify-between"
      >
        <span>{value || placeholder}</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transform transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-[#1a1d23] border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-auto">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 hover:text-white first:rounded-t-lg last:rounded-b-lg"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const MultiSelectDropdown = ({
    isOpen,
    setIsOpen,
    selected,
    options,
    onChange,
  }) => (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-left text-white focus:outline-none focus:border-[#7F56D9] flex items-center justify-between min-h-[38px]"
      >
        <div className="flex flex-wrap gap-1">
          {selected.map((label) => (
            <span
              key={label}
              className="px-2 py-1 bg-[#7F56D9] text-white text-xs rounded"
            >
              {label}
            </span>
          ))}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transform transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-[#1a1d23] border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-auto">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => {
                const newSelected = selected.includes(option)
                  ? selected.filter((item) => item !== option)
                  : [...selected, option];
                onChange(newSelected);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                selected.includes(option)
                  ? "bg-[#7F56D9] text-white"
                  : "text-gray-300"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const UserRow = ({
    user,
    isAdmin = false,
    onUpdateRole,
    onRemoveUser,
    onUpdateExtension,
  }) => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const isCurrentUser = currentUser && user.id === currentUser.id;
    const [editingExtension, setEditingExtension] = useState(false);
    const [extensionValue, setExtensionValue] = useState(
      user.extensionNumber || "",
    );

    const handleSaveExtension = async () => {
      try {
        await onUpdateExtension(user.id, extensionValue);
        setEditingExtension(false);
        toast.success("Extension updated successfully");
      } catch (error) {
        toast.error("Failed to update extension");
      }
    };
    const toggleDropdown = (userId) => {
      setOpenDropdown(openDropdown === userId ? null : userId);
    };

    return (
      <div className="flex items-center py-4 px-4 hover:bg-gray-800/50 transition-colors">
        {/* Avatar and Name */}
        <div className="flex items-center flex-1 min-w-0">
          <div className="relative mr-3">
            <img
              src={
                user.profileImageUrl ||
                `https://ui-avatars.com/api/?name=${user.name}&background=random`
              }
              alt={user.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            {isAdmin && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full border-2 border-[#0f1115]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-white font-medium text-sm truncate">
                {user.name}
              </p>
              {isAdmin && (
                <span className="text-xs text-purple-400 font-medium">
                  Admin
                </span>
              )}
            </div>
            <p className="text-gray-400 text-xs truncate">{user.email}</p>
          </div>
        </div>
        <div className="w-32 mr-4">
          {editingExtension ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={extensionValue}
                onChange={(e) => setExtensionValue(e.target.value)}
                className="w-20 px-2 py-1 bg-[#1a1d23] border border-gray-600 rounded text-white text-sm"
                placeholder="Ext"
              />
              <button
                onClick={handleSaveExtension}
                className="p-1 text-green-400 hover:text-green-300"
              >
                ✓
              </button>
              <button
                onClick={() => {
                  setEditingExtension(false);
                  setExtensionValue(user.extensionNumber || "");
                }}
                className="p-1 text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          ) : (
            <div
              onClick={() => setEditingExtension(true)}
              className="cursor-pointer text-gray-400 hover:text-white text-sm"
            >
              {user.extensionNumber || (
                <span className="text-gray-600 italic">No ext</span>
              )}
            </div>
          )}
        </div>

        {/* Date Added - Hidden on mobile */}
        <div className="hidden md:block w-32 text-gray-400 text-sm">
          {new Date(user.createdAt).toLocaleDateString()}
        </div>

        {/* Last Active - Hidden on mobile */}
        <div className="hidden md:block w-32 text-gray-400 text-sm">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString()
            : "Never"}
        </div>

        {/* Actions */}
        {(currentUser?.role === "admin" ||
          currentUser?.role === "super_admin") &&
          !isCurrentUser && (
            <div className="flex items-center gap-2 ml-4">
              <div className="relative">
                <button
                  onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                {showRoleDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#1a1d23] border border-gray-600 rounded-lg shadow-lg z-20">
                    <button
                      onClick={() => {
                        onUpdateRole(user.id, "admin");
                        setShowRoleDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white first:rounded-t-lg"
                    >
                      Make Admin
                    </button>
                    <button
                      onClick={() => {
                        onUpdateRole(user.id, "member");
                        setShowRoleDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white"
                    >
                      Make Member
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => onRemoveUser(user.id)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        {/* Invisible spacer for current user to maintain consistent spacing */}
        {isCurrentUser && (
          <div className="flex items-center gap-2 ml-4">
            <div className="relative">
              <div className="p-2 rounded-lg">
                <div className="w-4 h-4 invisible" />
              </div>
            </div>
            <div className="p-2 rounded-lg">
              <div className="w-4 h-4 invisible" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const SectionHeader = ({ title, description, count }) => (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-white text-lg font-medium">{title}</h2>
        <span className="text-gray-400 text-sm">({count})</span>
      </div>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  );

  const TableHeader = () => (
    <div className="flex items-center py-3 px-4 border-b border-gray-700">
      <div className="flex items-center mr-4">
        <input
          type="checkbox"
          className="appearance-none w-4 h-4 rounded border border-gray-600 bg-[#0C0E12] checked:bg-purple-500 checked:border-purple-500 focus:ring-purple-500 focus:ring-2"
        />
      </div>
      <div className="flex items-center flex-1">
        <span className="text-gray-400 text-sm font-medium">Name</span>
        <ChevronDown className="w-4 h-4 text-gray-400 ml-1" />
      </div>
      <div className="hidden md:block w-32">
        <span className="text-gray-400 text-sm font-medium">Date added</span>
      </div>
      <div className="hidden md:block w-32">
        <span className="text-gray-400 text-sm font-medium">Last active</span>
      </div>
      <div className="w-32"></div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "my-details":
        if (isLoadingUser) {
          return (
            <div className="rounded-lg pt-6">
              <div className="text-center py-8 text-gray-400">Loading...</div>
            </div>
          );
        }

        return (
          <div className="rounded-lg pt-6">
            {/* Header with buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-white text-lg font-medium mb-1">
                  Personal info
                </h2>
                <p className="text-gray-400 text-sm">
                  Update your photo and personal details here.
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    // Reset to original values
                    setPersonalDetails({
                      name: currentUser?.name || "",
                      email: currentUser?.email || "",
                      profileImageUrl: currentUser?.profileImageUrl || "",
                    });
                  }}
                  className="w-full sm:w-auto px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePersonalDetails}
                  className="w-full px-8 py-2 text-white rounded-lg font-medium transition-all duration-300  relative 
             hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                    border: "1px solid",
                    borderImageSource:
                      "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                    borderImageSlice: "1",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
                    e.currentTarget.style.boxShadow = `
      0px 42px 36px -12px #8D5EE680,
      0px 12px 20px 0px #7F56D93D,
      0px 4px 8px -4px #7F56D928,
      0px 12px 28px -12px #7F56D9 inset,
      0px -28px 36px 0px #FFFFFF4D inset,
      0px 0px 16px 0px #FFFFFF inset
    `;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
                    e.currentTarget.style.boxShadow = `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `;
                  }}
                >
                  Save
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Name Field */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
                  Name *
                </label>
                <div className="w-full sm:w-3/5">
                  <input
                    type="text"
                    value={personalDetails.name}
                    onChange={(e) =>
                      setPersonalDetails({
                        ...personalDetails,
                        name: e.target.value,
                      })
                    }
                    className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
                  Email address
                </label>
                <div className="w-full sm:w-3/5 flex items-center">
                  <input
                    type="email"
                    value={personalDetails.email}
                    onChange={(e) =>
                      setPersonalDetails({
                        ...personalDetails,
                        email: e.target.value,
                      })
                    }
                    className="flex-1 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Photo Upload */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <div className="w-full sm:w-2/5">
                  <label className="block text-gray-300 text-sm mb-2">
                    Your photo
                  </label>
                  <p className="text-gray-400 text-xs">
                    This will be displayed on your profile.
                  </p>
                </div>
                <div className="w-full sm:w-3/5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <img
                      src={
                        personalDetails.profileImageUrl ||
                        `https://ui-avatars.com/api/?name=${personalDetails.name}&background=random`
                      }
                      alt="Profile"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 sm:p-6 flex-1 max-w-md">
                      <input
                        type="file"
                        id="profile-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleProfileImageUpload(file);
                          }
                        }}
                      />
                      <label
                        htmlFor="profile-upload"
                        className="cursor-pointer"
                      >
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                          <p className="text-white text-sm mb-1">
                            <span className="font-medium">Click to upload</span>{" "}
                            or drag and drop
                          </p>
                          <p className="text-gray-400 text-xs">
                            SVG, PNG or JPG (max. 800×400 px)
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Role Field - Read Only */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
                  Role
                </label>
                <input
                  type="text"
                  value={currentUser?.role || ""}
                  readOnly
                  className="w-full sm:w-3/5 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-gray-400 cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        );

      case "workspace":
        if (
          currentUser?.role !== "admin" &&
          currentUser?.role !== "super_admin"
        ) {
          return (
            <div className="rounded-lg pt-6">
              <div className="text-center py-8 text-gray-400">
                You don't have permission to access this section.
              </div>
            </div>
          );
        }

        if (isLoadingUser) {
          return (
            <div className="rounded-lg pt-6">
              <div className="text-center py-8 text-gray-400">Loading...</div>
            </div>
          );
        }

        return (
          <div className="rounded-lg pt-6">
            {/* Header with buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-white text-lg font-medium mb-1">
                  Workspace
                </h2>
                <p className="text-gray-400 text-sm">
                  Update your company logo and details here.
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    // Reset to original values
                    if (currentUser?.company) {
                      setWorkspaceDetails({
                        companyName: currentUser.company.name || "",
                        workspaceUrl: currentUser.company.workspaceUrl || "",
                        logoUrl: currentUser.company.logoUrl || "",
                      });
                    }
                  }}
                  className="w-full sm:w-auto px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateWorkspaceDetails}
                  className="w-full px-8 py-2 text-white rounded-lg font-medium transition-all duration-300  relative 
             hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                    border: "1px solid",
                    borderImageSource:
                      "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                    borderImageSlice: "1",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
                    e.currentTarget.style.boxShadow = `
      0px 42px 36px -12px #8D5EE680,
      0px 12px 20px 0px #7F56D93D,
      0px 4px 8px -4px #7F56D928,
      0px 12px 28px -12px #7F56D9 inset,
      0px -28px 36px 0px #FFFFFF4D inset,
      0px 0px 16px 0px #FFFFFF inset
    `;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
                    e.currentTarget.style.boxShadow = `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `;
                  }}
                >
                  Save
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Company Name Field */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
                  Company name
                </label>
                <div className="w-full sm:w-3/5 flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    value={workspaceDetails.companyName}
                    onChange={(e) =>
                      setWorkspaceDetails({
                        ...workspaceDetails,
                        companyName: e.target.value,
                      })
                    }
                    className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Workspace URL Field */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <label className="block text-gray-300 text-sm mb-2 w-full sm:w-2/5">
                  Workspace URL
                </label>
                <div className="w-full sm:w-3/5 flex items-center">
                  <input
                    type="text"
                    value={workspaceDetails.workspaceUrl}
                    onChange={(e) =>
                      setWorkspaceDetails({
                        ...workspaceDetails,
                        workspaceUrl: e.target.value,
                      })
                    }
                    className="flex-1 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 pb-8">
                <div className="w-full sm:w-2/5">
                  <label className="block text-gray-300 text-sm mb-2">
                    Company Logo
                  </label>
                  <p className="text-gray-400 text-xs">
                    Update your company logo.
                  </p>
                </div>
                <div className="w-full sm:w-3/5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <img
                      src={
                        workspaceDetails.logoUrl ||
                        `https://ui-avatars.com/api/?name=${workspaceDetails.companyName}&background=random`
                      }
                      alt="Company Logo"
                      className="w-16 h-16 rounded-md object-cover"
                    />
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 sm:p-6 flex-1 max-w-md">
                      <input
                        type="file"
                        id="logo-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleCompanyLogoUpload(file);
                          }
                        }}
                      />
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                          <p className="text-white text-sm mb-1">
                            <span className="font-medium">Click to upload</span>{" "}
                            or drag and drop
                          </p>
                          <p className="text-gray-400 text-xs">
                            SVG, PNG or JPG (max. 800×400 px)
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "integrations":
        return (
          <div className=" min-h-screen">
            <div className="max-w-full mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                  <h2 className="text-white text-lg font-medium mb-1">
                    Integrations and connected apps
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Connect the tool you use everyday.
                  </p>
                </div>
              </div>

              {isLoadingIntegrations ? (
                <div className="text-center py-8 text-gray-400">
                  Loading integrations...
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {integrations.map((integration) => (
                    <div
                      key={integration.id}
                      className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-4 sm:p-5 flex flex-col"
                    >
                      <div className="flex items-start justify-between mb-10">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                            <img
                              src={getLogoForIntegration(integration.id)}
                              alt={`${integration.name} logo`}
                              className="w-8 h-8 object-contain"
                            />
                          </div>
                          <h3 className="text-white font-medium">
                            {integration.name}
                          </h3>
                        </div>

                        <button
                          onClick={() => handleIntegrationToggle(integration)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            integrationStatuses[integration.name]
                              ? "bg-[#7F56D9]"
                              : "bg-gray-600"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              integrationStatuses[integration.name]
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      <p className="text-gray-400 text-sm flex-grow mb-10">
                        {integrationStatuses[integration.name]
                          ? `Connected to ${integration.name}`
                          : `Connect your ${integration.name} account to sync meetings automatically`}
                      </p>

                      <button
                        onClick={() => handleIntegrationToggle(integration)}
                        className="text-gray-400 hover:text-white text-sm transition-colors text-end mt-10 border-t border-[#22262F] pt-5"
                      >
                        {integrationStatuses[integration.name]
                          ? "Manage integration"
                          : "View integration"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      case "email-notifications":
        if (
          currentUser?.role !== "admin" &&
          currentUser?.role !== "super_admin"
        ) {
          return (
            <div className="rounded-lg pt-6">
              <div className="text-center py-8 text-gray-400">
                You don't have permission to access this section.
              </div>
            </div>
          );
        }

        return (
          <div className="rounded-lg pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-white text-lg font-medium mb-1">
                  Email Notification Settings
                </h2>
                <p className="text-gray-400 text-sm">
                  Configure who receives email notifications for different
                  events
                </p>
              </div>
            </div>

            {loadingConfigs ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : (
              <div className="space-y-4">
                <NotificationConfigCard
                  title="Daily Reports"
                  description="Receive daily call analytics and performance summaries"
                  icon="📊"
                  notificationType="daily_report"
                  config={notificationConfigs.find(
                    (c) => c.notificationType === "daily_report",
                  )}
                  onUpdate={handleUpdateNotificationConfig}
                />

                <NotificationConfigCard
                  title="Weekly Reports"
                  description="Comprehensive weekly performance analysis and insights"
                  icon="📈"
                  notificationType="weekly_report"
                  config={notificationConfigs.find(
                    (c) => c.notificationType === "weekly_report",
                  )}
                  onUpdate={handleUpdateNotificationConfig}
                />

                <NotificationConfigCard
                  title="Flagged Calls"
                  description="Alerts for calls requiring immediate attention or review"
                  icon="🚨"
                  notificationType="flagged_call"
                  config={notificationConfigs.find(
                    (c) => c.notificationType === "flagged_call",
                  )}
                  onUpdate={handleUpdateNotificationConfig}
                />

                <NotificationConfigCard
                  title="High Quality Leads"
                  description="Notifications for high-scoring qualified leads"
                  icon="🌟"
                  notificationType="high_quality_lead"
                  config={notificationConfigs.find(
                    (c) => c.notificationType === "high_quality_lead",
                  )}
                  onUpdate={handleUpdateNotificationConfig}
                />

                <NotificationConfigCard
                  title="Appointments Booked"
                  description="Instant notifications when appointments are scheduled"
                  icon="📅"
                  notificationType="appointment_booked"
                  config={notificationConfigs.find(
                    (c) => c.notificationType === "appointment_booked",
                  )}
                  onUpdate={handleUpdateNotificationConfig}
                />
              </div>
            )}
          </div>
        );
      case "ai-preferences":
        return (
          <div className="min-h-screen bg-[#0f1115] text-white ">
            <div className=" mx-auto">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                  <h2 className="text-white text-lg font-medium mb-1">
                    AI preferences
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Connect the tool you use everyday.
                  </p>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button className="flex-shrink-0 whitespace-nowrap px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors">
                    Reset to default
                  </button>
                  <button className="flex-shrink-0 whitespace-nowrap px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors">
                    Cancel
                  </button>
                  <button
                    className="flex-shrink-0 whitespace-nowrap px-8 py-2 text-white rounded-lg font-medium transition-all duration-300 relative 
             hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base"
                    style={{
                      background:
                        "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                      border: "1px solid",
                      borderImageSource:
                        "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                      boxShadow: `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                      borderImageSlice: "1",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
                      e.currentTarget.style.boxShadow = `
      0px 42px 36px -12px #8D5EE680,
      0px 12px 20px 0px #7F56D93D,
      0px 4px 8px -4px #7F56D928,
      0px 12px 28px -12px #7F56D9 inset,
      0px -28px 36px 0px #FFFFFF4D inset,
      0px 0px 16px 0px #FFFFFF inset
    `;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
                      e.currentTarget.style.boxShadow = `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `;
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>

              <div className="space-y-8">
                {/* Enable summarisation */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Enable summarisation
                    </h3>
                  </div>
                  <Toggle
                    enabled={enableSummarisation}
                    onChange={setEnableSummarisation}
                  />
                </div>

                {/* Model for summarisation */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Model for summarisation
                    </h3>
                  </div>
                  <div className="flex-1 ">
                    <Dropdown
                      isOpen={isModelDropdownOpen}
                      setIsOpen={setIsModelDropdownOpen}
                      value={selectedModel}
                      options={models}
                      onChange={setSelectedModel}
                      placeholder="Select model"
                    />
                  </div>
                </div>

                {/* Summary tone */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Summary tone
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Summaries can be generated immediately post call or
                      manually from the transcript page.
                    </p>
                  </div>
                  <div className="flex-1">
                    <Dropdown
                      isOpen={isToneDropdownOpen}
                      setIsOpen={setIsToneDropdownOpen}
                      value={summaryTone}
                      options={tones}
                      onChange={setSummaryTone}
                      placeholder="Select tone"
                    />
                  </div>
                </div>

                {/* Enable action items */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Enable action items
                    </h3>
                  </div>
                  <Toggle
                    enabled={enableActionItems}
                    onChange={setEnableActionItems}
                  />
                </div>

                {/* Default labels */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Default labels
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Auto-detect tasks and responsibilities directly from
                      conversations.
                    </p>
                  </div>
                  <div className="flex-1">
                    <MultiSelectDropdown
                      isOpen={isLabelsDropdownOpen}
                      setIsOpen={setIsLabelsDropdownOpen}
                      selected={selectedLabels}
                      options={availableLabels}
                      onChange={setSelectedLabels}
                    />
                  </div>
                </div>

                {/* Enable sentiment detection */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Enable sentiment detection
                    </h3>
                  </div>
                  <Toggle
                    enabled={enableSentimentDetection}
                    onChange={setEnableSentimentDetection}
                  />
                </div>

                {/* Target speaker */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Target speaker
                    </h3>
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-4">
                      {["Agent", "Customer", "Both"].map((option) => (
                        <label
                          key={option}
                          className="flex items-center cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="targetSpeaker"
                            value={option}
                            checked={targetSpeaker === option}
                            onChange={(e) => setTargetSpeaker(e.target.value)}
                            className="sr-only"
                          />
                          <div
                            className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                              targetSpeaker === option
                                ? "border-[#7F56D9]"
                                : "border-gray-500"
                            }`}
                          >
                            {targetSpeaker === option && (
                              <div className="w-2 h-2 rounded-full bg-[#7F56D9]" />
                            )}
                          </div>
                          <span className="text-white text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Enable auto tagging */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Enable auto tagging
                    </h3>
                  </div>
                  <Toggle
                    enabled={enableAutoTagging}
                    onChange={setEnableAutoTagging}
                  />
                </div>

                {/* Training set upload */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">
                      Training set upload
                    </h3>
                  </div>
                  <div className="flex-1 ">
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <p className="text-white text-sm mb-1">
                          <span className="font-medium">Click to upload</span>{" "}
                          or drag and drop
                        </p>
                        <p className="text-gray-400 text-xs">CSV or XLSX</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "team":
        // Group users by role
        const adminUsers = teamMembers.filter(
          (user) => user.role === "admin" || user.role === "super_admin",
        );
        const regularUsers = teamMembers.filter(
          (user) => user.role === "member",
        );

        if (
          currentUser?.role !== "admin" &&
          currentUser?.role !== "super_admin"
        ) {
          return (
            <div className="rounded-lg pt-6">
              <div className="text-center py-8 text-gray-400">
                You don't have permission to access this section.
              </div>
            </div>
          );
        }

        return (
          <div className="min-h-screen text-white">
            <div className="mx-auto">
              {/* Admin Users Section */}
              <div className="mb-12">
                <div className="flex justify-between items-center mb-6">
                  <SectionHeader
                    title="Admin users"
                    description="Admins can add and remove users and manage organizational settings."
                    count={adminUsers.length}
                  />
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
                    style={{
                      background:
                        "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                      border: "1px solid",
                      borderImageSource:
                        "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                      boxShadow: `
              0px 36px 32px -16px #7F56D936,
              0px 8px 16px 0px #7F56D91F,
              0px 2px 4px -8px #7F56D914,
              0px 8px 24px -16px #FFFFFF3D inset,
              0px -24px 32px 0px #FFFFFF38 inset,
              0px 0px 12px 0px #FFFFFF inset
            `,
                      borderImageSlice: "1",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
                      e.currentTarget.style.boxShadow = `
              0px 42px 36px -12px #8D5EE680,
              0px 12px 20px 0px #7F56D93D,
              0px 4px 8px -4px #7F56D928,
              0px 12px 28px -12px #7F56D9 inset,
              0px -28px 36px 0px #FFFFFF4D inset,
              0px 0px 16px 0px #FFFFFF inset
            `;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
                      e.currentTarget.style.boxShadow = `
              0px 36px 32px -16px #7F56D936,
              0px 8px 16px 0px #7F56D91F,
              0px 2px 4px -8px #7F56D914,
              0px 8px 24px -16px #FFFFFF3D inset,
              0px -24px 32px 0px #FFFFFF38 inset,
              0px 0px 12px 0px #FFFFFF inset
            `;
                    }}
                  >
                    Invite Team Member
                  </button>
                </div>

                {isLoading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : (
                  <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
                    <TableHeader />
                    <div className="divide-y divide-gray-700">
                      {adminUsers.map((user) => (
                        <UserRow
                          key={user.id}
                          user={user}
                          isAdmin={true}
                          onUpdateRole={handleUpdateRole}
                          onRemoveUser={handleRemoveUser}
                          onUpdateExtension={handleUpdateExtension}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Regular Users Section */}
              <div className="mb-12">
                <SectionHeader
                  title="Team members"
                  description="Team members can access and review this organization's data."
                  count={regularUsers.length}
                />

                {isLoading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : (
                  <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
                    <TableHeader />
                    <div className="divide-y divide-gray-700">
                      {regularUsers.map((user) => (
                        <UserRow
                          key={user.id}
                          user={user}
                          isAdmin={false}
                          onUpdateRole={handleUpdateRole}
                          onRemoveUser={handleRemoveUser}
                          onUpdateExtension={handleUpdateExtension}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Pending Invitations */}
              <div>
                <SectionHeader
                  title="Pending invitations"
                  description="Invitations that haven't been accepted yet."
                  count={pendingInvitations.length}
                />

                {isLoading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : pendingInvitations.length > 0 ? (
                  <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
                    <div className="grid grid-cols-12 gap-4 py-3 px-4 border-b border-gray-700">
                      <div className="col-span-5">
                        <span className="text-gray-400 text-sm font-medium">
                          Email
                        </span>
                      </div>
                      <div className="col-span-3">
                        <span className="text-gray-400 text-sm font-medium">
                          Role
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-400 text-sm font-medium">
                          Expires
                        </span>
                      </div>
                      <div className="col-span-2"></div>
                    </div>
                    <div className="divide-y divide-gray-700">
                      {pendingInvitations.map((invite) => (
                        <div
                          key={invite.id}
                          className="grid grid-cols-12 gap-4 py-4 px-4"
                        >
                          <div className="col-span-5">
                            <p className="text-white">{invite.email}</p>
                          </div>
                          <div className="col-span-3">
                            <span className="capitalize text-gray-400">
                              {invite.role}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-400">
                              {new Date(invite.expiresAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="col-span-2 flex justify-end gap-2">
                            <button
                              onClick={() => handleResendInvitation(invite.id)}
                              className="text-purple-400 hover:text-purple-300"
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => handleCancelInvitation(invite.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    No pending invitations
                  </div>
                )}
              </div>

              {/* Invite Modal */}
              {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-[#0C0E12] rounded-lg p-6 w-full max-w-md border border-gray-700">
                    <h3 className="text-lg font-medium mb-4">
                      Invite Team Member
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-300 text-sm mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm mb-1">
                          Role
                        </label>
                        <select
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={() => setShowInviteModal(false)}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleInviteTeamMember}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                        disabled={!inviteEmail}
                      >
                        Send Invitation
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-semibold mb-4 sm:mb-0">Settings</h1>
        </div>

        {/* Tabs */}
        <div className=" mb-6 overflow-x-auto">
          <nav className="flex space-x-6 sm:space-x-8 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "text-[#CECFD2] bg-[#22262F] px-3 py-2 rounded-lg"
                    : "text-gray-400 hover:text-gray-300 px-3 py-2"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        <AskGenziButton />
      </div>
    </div>
  );
}

export default SettingsPage;
